import type { BrightnessId, MutagenId, NormalizedChart, NormalizedPalace, NormalizedStar, PalaceId } from './domain.js';
import type { Feature } from './rules.js';
import { sha256Fingerprint } from './stable.js';

export type RelationshipContextType = 'partner' | 'siblings' | 'parent_to_child' | 'child_to_parent';

export const RELATIONSHIP_CONTEXT_TYPES: readonly RelationshipContextType[] = Object.freeze([
  'partner',
  'siblings',
  'parent_to_child',
  'child_to_parent',
]);

export interface RelationshipFocusMapping {
  type: RelationshipContextType;
  personAFocusPalaceId: PalaceId;
  personBFocusPalaceId: PalaceId;
  directionLabel: string;
}

export interface RelationshipPersonContext {
  role: 'A' | 'B';
  chartFingerprint: string;
  calculationRulesetId: string;
  calculationRulesetVersion: string;
  focusPalaceId: PalaceId;
  focusPalaceLabel: string;
  starIds: readonly string[];
  starLabels: readonly string[];
  birthYearTransformationStarIds: readonly string[];
  focusStars: readonly RelationshipFocusStarFact[];
  relatedPalaces: readonly RelationshipRelatedPalaceFact[];
  focusTransformations: readonly RelationshipTransformationFact[];
}

export interface RelationshipFocusStarFact {
  starId: string;
  starLabel: string;
  brightness: BrightnessId;
  mutagens: readonly MutagenId[];
}

export interface RelationshipRelatedPalaceFact {
  relationType: 'trine' | 'opposite';
  palaceId: PalaceId;
  palaceLabel: string;
  starIds: readonly string[];
  starLabels: readonly string[];
}

export interface RelationshipTransformationFact {
  direction: 'outgoing' | 'incoming';
  sourcePalaceId: PalaceId;
  targetPalaceId: PalaceId;
  starId: string;
  mutagen: MutagenId;
}

export interface RelationshipContextBundle {
  model: 'RELATIONSHIP-CONTEXT-0.1.0';
  relationshipType: RelationshipContextType;
  mapping: RelationshipFocusMapping;
  personA: RelationshipPersonContext;
  personB: RelationshipPersonContext;
  contextFingerprint: string;
  disclosure: string;
}

const FOCUS_MAPPINGS: Readonly<Record<RelationshipContextType, RelationshipFocusMapping>> = Object.freeze({
  partner: Object.freeze({ type:'partner', personAFocusPalaceId:'spouse', personBFocusPalaceId:'spouse', directionLabel:'雙方夫妻宮互看' }),
  siblings: Object.freeze({ type:'siblings', personAFocusPalaceId:'siblings', personBFocusPalaceId:'siblings', directionLabel:'雙方兄弟宮互看' }),
  parent_to_child: Object.freeze({ type:'parent_to_child', personAFocusPalaceId:'children', personBFocusPalaceId:'parents', directionLabel:'父母端子女宮 → 子女端父母宮' }),
  child_to_parent: Object.freeze({ type:'child_to_parent', personAFocusPalaceId:'parents', personBFocusPalaceId:'children', directionLabel:'子女端父母宮 → 父母端子女宮' }),
});

function allStars(palace: NormalizedPalace): readonly NormalizedStar[] {
  return [...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars];
}

function contextFor(chart: NormalizedChart, role: 'A' | 'B', palaceId: PalaceId): RelationshipPersonContext {
  const palace = chart.palaces[palaceId];
  if (!palace) throw new Error(`Relationship context missing palace ${palaceId}`);
  const stars = allStars(palace);
  const focusStars = stars.map((star): RelationshipFocusStarFact => Object.freeze({
    starId: star.id,
    starLabel: star.label,
    brightness: star.brightness,
    mutagens: Object.freeze([...star.mutagens].sort()),
  })).sort((a,b)=>a.starId.localeCompare(b.starId));
  const relatedPalaces = chart.relations
    .filter((edge) => edge.from === palaceId && (edge.type === 'trine' || edge.type === 'opposite'))
    .map((edge): RelationshipRelatedPalaceFact => {
      const target = chart.palaces[edge.to];
      if (!target) throw new Error(`Relationship context missing related palace ${edge.to}`);
      const targetStars = allStars(target);
      return Object.freeze({
        relationType: edge.type as 'trine'|'opposite',
        palaceId: target.id,
        palaceLabel: target.label,
        starIds: Object.freeze([...new Set(targetStars.map((star)=>star.id))].sort()),
        starLabels: Object.freeze([...new Set(targetStars.map((star)=>star.label))].sort()),
      });
    })
    .sort((a,b)=>`${a.relationType}:${a.palaceId}`.localeCompare(`${b.relationType}:${b.palaceId}`));
  const focusTransformations = chart.natalTransformations
    .filter((edge) => edge.sourcePalaceId === palaceId || edge.targetPalaceId === palaceId)
    .flatMap((edge): RelationshipTransformationFact[] => {
      const facts: RelationshipTransformationFact[] = [];
      if (edge.sourcePalaceId === palaceId) facts.push({ direction:'outgoing', sourcePalaceId:edge.sourcePalaceId, targetPalaceId:edge.targetPalaceId, starId:edge.starId, mutagen:edge.mutagen });
      if (edge.targetPalaceId === palaceId) facts.push({ direction:'incoming', sourcePalaceId:edge.sourcePalaceId, targetPalaceId:edge.targetPalaceId, starId:edge.starId, mutagen:edge.mutagen });
      return facts;
    })
    .map((fact)=>Object.freeze(fact))
    .sort((a,b)=>`${a.direction}:${a.sourcePalaceId}:${a.mutagen}:${a.targetPalaceId}:${a.starId}`.localeCompare(`${b.direction}:${b.sourcePalaceId}:${b.mutagen}:${b.targetPalaceId}:${b.starId}`));
  return Object.freeze({
    role,
    chartFingerprint: chart.calculation.chartFingerprint,
    calculationRulesetId: chart.calculation.rulesetId,
    calculationRulesetVersion: chart.calculation.rulesetVersion,
    focusPalaceId: palaceId,
    focusPalaceLabel: palace.label,
    starIds: Object.freeze([...new Set(stars.map((star)=>star.id))].sort()),
    starLabels: Object.freeze([...new Set(stars.map((star)=>star.label))].sort()),
    birthYearTransformationStarIds: Object.freeze([...new Set(stars.filter((star)=>star.mutagens.length>0).map((star)=>star.id))].sort()),
    focusStars: Object.freeze(focusStars),
    relatedPalaces: Object.freeze(relatedPalaces),
    focusTransformations: Object.freeze(focusTransformations),
  });
}

export function relationshipFocusMapping(type: RelationshipContextType): RelationshipFocusMapping {
  return FOCUS_MAPPINGS[type];
}

export async function buildRelationshipContext(
  personAChart: NormalizedChart,
  personBChart: NormalizedChart,
  relationshipType: RelationshipContextType,
): Promise<RelationshipContextBundle> {
  const mapping = relationshipFocusMapping(relationshipType);
  const personA = contextFor(personAChart,'A',mapping.personAFocusPalaceId);
  const personB = contextFor(personBChart,'B',mapping.personBFocusPalaceId);
  const fingerprintBasis = {
    model:'RELATIONSHIP-CONTEXT-0.1.0',
    relationshipType,
    personAChartFingerprint:personA.chartFingerprint,
    personBChartFingerprint:personB.chartFingerprint,
    personACalculationRuleset:`${personA.calculationRulesetId}@${personA.calculationRulesetVersion}`,
    personBCalculationRuleset:`${personB.calculationRulesetId}@${personB.calculationRulesetVersion}`,
    personAFocusPalaceId:personA.focusPalaceId,
    personBFocusPalaceId:personB.focusPalaceId,
  } as const;
  return Object.freeze({
    model:'RELATIONSHIP-CONTEXT-0.1.0',
    relationshipType,
    mapping,
    personA,
    personB,
    contextFingerprint: await sha256Fingerprint(fingerprintBasis),
    disclosure:'雙人關係 Context 目前只整理兩份本命盤在指定關係中的客觀宮位與星曜證據；不輸出相合百分比、科學相容度或必然關係結果。',
  });
}

function relationshipEvidenceRef(role: 'A'|'B', palaceId: PalaceId, starId: string): string {
  return `RELATIONSHIP:${role}:PALACE:${palaceId}:STAR:${starId}`.toUpperCase();
}

export function extractRelationshipFeatures(context: RelationshipContextBundle): readonly Feature[] {
  const features: Feature[] = [];
  for (const person of [context.personA, context.personB] as const) {
    const role = person.role.toLowerCase();
    features.push({
      id:`relationship.${role}.focus_palace.${person.focusPalaceId}`,
      scope:'natal',
      kind:'relationship_focus_palace',
      value:true,
      evidenceRefs:[`RELATIONSHIP:${person.role}:FOCUS:${person.focusPalaceId}`.toUpperCase()],
      evidenceGroup:`relationship-${role}-focus-palace`,
    });
    for (const starId of person.starIds) {
      features.push({
        id:`relationship.${role}.focus.has_star.${starId}`,
        scope:'natal',
        kind:'relationship_focus_star',
        value:true,
        evidenceRefs:[relationshipEvidenceRef(person.role,person.focusPalaceId,starId)],
        evidenceGroup:`relationship-${role}-focus-stars`,
      });
    }
    for (const star of person.focusStars) {
      features.push({
        id:`relationship.${role}.focus.star.${star.starId}.brightness.${star.brightness}`,
        scope:'natal',
        kind:'relationship_focus_star_brightness',
        value:true,
        evidenceRefs:[`RELATIONSHIP:${person.role}:PALACE:${person.focusPalaceId}:STAR:${star.starId}:BRIGHTNESS:${star.brightness}`.toUpperCase()],
        evidenceGroup:`relationship-${role}-focus-brightness`,
      });
      for (const mutagen of star.mutagens) {
        features.push({
          id:`relationship.${role}.focus.star.${star.starId}.birth_year_mutagen.${mutagen}`,
          scope:'natal',
          kind:'relationship_focus_birth_year_mutagen',
          value:true,
          evidenceRefs:[`RELATIONSHIP:${person.role}:PALACE:${person.focusPalaceId}:STAR:${star.starId}:MUTAGEN:${mutagen}`.toUpperCase()],
          evidenceGroup:`relationship-${role}-focus-birth-year-transformations`,
        });
      }
    }
    for (const related of person.relatedPalaces) {
      for (const starId of related.starIds) {
        features.push({
          id:`relationship.${role}.focus.${related.relationType}.${related.palaceId}.has_star.${starId}`,
          scope:'natal',
          kind:'relationship_related_palace_star',
          value:true,
          evidenceRefs:[
            `RELATIONSHIP:${person.role}:RELATION:${person.focusPalaceId}:${related.relationType}:${related.palaceId}`.toUpperCase(),
            relationshipEvidenceRef(person.role,related.palaceId,starId),
          ],
          evidenceGroup:`relationship-${role}-surrounding-palaces`,
        });
      }
    }
    for (const edge of person.focusTransformations) {
      features.push({
        id:`relationship.${role}.focus.palace_stem_flight.${edge.direction}.${edge.sourcePalaceId}.${edge.mutagen}.${edge.targetPalaceId}.${edge.starId}`,
        scope:'natal',
        kind:'relationship_focus_palace_stem_transformation',
        value:true,
        evidenceRefs:[`RELATIONSHIP:${person.role}:PALACE-STEM-FLIGHT:${edge.direction}:${edge.sourcePalaceId}:${edge.mutagen}:${edge.targetPalaceId}:${edge.starId}`.toUpperCase()],
        evidenceGroup:`relationship-${role}-focus-palace-stem-transformations`,
      });
    }
  }

  const shared = context.personA.starIds.filter((starId)=>context.personB.starIds.includes(starId));
  for (const starId of shared) {
    features.push({
      id:`relationship.both.focus.has_star.${starId}`,
      scope:'natal',
      kind:'relationship_shared_focus_star',
      value:true,
      evidenceRefs:[
        relationshipEvidenceRef('A',context.personA.focusPalaceId,starId),
        relationshipEvidenceRef('B',context.personB.focusPalaceId,starId),
      ],
      evidenceGroup:'relationship-shared-focus-stars',
    });
  }

  return features.sort((a,b)=>a.id.localeCompare(b.id));
}
